/**
 * Shared card shell for the dashboard widgets. Mirrors the design's `.card` +
 * `.card-head` (title, subtitle, and an optional right-aligned "Open X" link).
 */
import { ChevronRight } from "lucide-react";
import type { ReactNode } from "react";
import { Link } from "react-router-dom";

import { cn } from "@/lib/utils";

interface DashboardCardProps {
  title: string;
  subtitle: ReactNode;
  /** Optional "Open X →" link shown at the right of the header. */
  link?: { label: string; to: string };
  className?: string;
  bodyClassName?: string;
  children: ReactNode;
}

export function DashboardCard({
  title,
  subtitle,
  link,
  className,
  bodyClassName,
  children,
}: DashboardCardProps) {
  return (
    <div
      className={cn(
        "min-w-0 rounded-xl border border-border bg-card px-5 py-[18px]",
        className,
      )}
    >
      <div className="mb-4 flex items-start justify-between gap-4">
        <div>
          <h3 className="m-0 text-[15px] font-bold text-foreground">{title}</h3>
          <div className="mt-[3px] text-[12.5px] text-muted-foreground">
            {subtitle}
          </div>
        </div>
        {link ? (
          <Link
            to={link.to}
            className="inline-flex flex-shrink-0 items-center gap-1 whitespace-nowrap text-[12.5px] font-semibold text-muted-foreground transition-colors hover:text-primary"
          >
            {link.label}
            <ChevronRight className="h-[13px] w-[13px]" aria-hidden />
          </Link>
        ) : null}
      </div>
      <div className={bodyClassName}>{children}</div>
    </div>
  );
}
